import {
  LayoutConfig,
  LocalStorageKey,
  OfferStatusEnum,
  RecruitmentStageEnum,
  ReturnLayoutData,
  TalentSchedule,
  TalentScheduleStatusEnum,
  defaultNewsTenants,
} from "@/services/constants";
import {
  IMAGE_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  PDF_MIME_TYPE,
} from "@mantine/dropzone";
import { CondOperator, RequestQueryBuilder } from "@nestjsx/crud-request";
import axios, { AxiosResponse } from "axios";
import convert from "color-convert";
import createDOMPurify from "dompurify";
import _, { isArray, isNil } from "lodash";
import { generateToken } from "services/otplibServices";
import { PortalJsonata } from "./jsonata-functions/portal-jsonata";
axios.defaults.headers.common["ngrok-skip-browser-warning"] = "true";
export const api = () => {
  return process.env.NEXT_PUBLIC_API_URL;
};

export const purfifyHTML = (html: string, fallback: string = "") => {
  if (!html) return "";

  let cleanHtml = fallback;
  if (typeof window !== "undefined") {
    const DOMPurify = createDOMPurify(window);

    // Hook: filter data: URIs by MIME type
    DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
      if (data.attrName === "href" && data.attrValue.startsWith("data:")) {
        const mime = data.attrValue.split(",")[0].split(":")[1].split(";")[0];

        const allowedMimes: string[] = [
          ...IMAGE_MIME_TYPE,
          ...PDF_MIME_TYPE,
          ...MS_WORD_MIME_TYPE,
          ...MS_EXCEL_MIME_TYPE,
        ];

        if (!allowedMimes.includes(mime)) {
          data.keepAttr = false; // remove the href if not allowed
        }
      }

      // Hook: only allow YouTube iframe sources
      if (node.nodeName === "IFRAME" && data.attrName === "src") {
        if (!/^https:\/\/www\.youtube\.com\/embed\//.test(data.attrValue)) {
          data.keepAttr = false; // strip src if not YouTube embed
        }
      }
    });

    cleanHtml = DOMPurify.sanitize(html, {
      // ALLOWED_TAGS: [
      //   "div",
      //   "iframe",
      //   "a",
      //   "img",
      //   "p",
      //   "span",
      //   "strong",
      //   "em",
      //   "ul",
      //   "li",
      //   "ol",
      //   "br",
      //   "hr",
      // ],
      ALLOWED_ATTR: [
        "href",
        "src",
        "style",
        "width",
        "height",
        "frameborder",
        "allow",
        "allowfullscreen",
        "download",
        "target",
        "ref",
      ],
      ALLOWED_URI_REGEXP:
        /^(?:(?:https?|mailto|ftp|tel|file|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });
  }

  return cleanHtml;
};

export async function getLayout(clearStorage = true): Promise<{
  layout: { config: LayoutConfig; headers: any; footers: any; pages: any[] };
  tenant: string;
  articleOption?: { url?: string; query?: string };
} | null> {
  let actualHostName = api() || `/api`;

  const getLayoutHelper = (layoutParam: any) => {
    const convertColor = convert.hex.hsl(layoutParam.config.primaryColor);
    return {
      config: {
        ...layoutParam.config,
        hoverColor: `hsl(${convertColor[0]}, ${convertColor[1]}%, ${
          convertColor[2] + 10
        }%)`,
        focusColor: `hsl(${convertColor[0]}, ${convertColor[1]}%, ${
          convertColor[2] - 10
        }%)`,
        boxShadowColor: `hsla(${convertColor[0]}, ${convertColor[1]}%, ${convertColor[2]}%, 0.5)`,
        lightBoxShadowColor: `hsla(${convertColor[0]}, ${convertColor[1]}%, ${convertColor[2]}%, 0.2)`,
        primaryBasedBackground: `hsla(${convertColor[0]}, ${convertColor[1]}%, ${convertColor[2]}%, 0.1)`,
      },
      headers: layoutParam.headers,
      pages: layoutParam.pages,
      footers: layoutParam.footers,
    };
  };

  // return {
  //   layout: getLayoutHelper(layoutTest.layout),
  //   tenant: layoutTest.tenant,
  // };
  const getLayoutApi = axios
    .get(`${actualHostName}/portals/layouts`, {
      headers: {
        "x-dynamic-token": generateToken(),
      },
    })
    .catch((error) => null);
  const apiResponse: AxiosResponse<ReturnLayoutData> | null =
    await getLayoutApi;
  if (apiResponse) {
    // console.log("apiResponse", apiResponse);
    const layoutFromApi = apiResponse.data.layout;
    if (
      layoutFromApi &&
      layoutFromApi.config &&
      layoutFromApi.config.primaryColor
    ) {
      const tenant = apiResponse.data.tenant || "";
      if (typeof window !== "undefined") {
        if (clearStorage) window.localStorage.clear();
        window.localStorage.setItem(LocalStorageKey.TENANT, tenant);
        if (layoutFromApi.config.fileUrl) {
          window.localStorage.setItem(
            LocalStorageKey.FILE_URL,
            layoutFromApi.config.fileUrl,
          );
          window.localStorage.setItem(
            LocalStorageKey.USE_FILE_URL_CONFIG,
            "true",
          );
        }

        if (layoutFromApi.config.article) {
          const url = layoutFromApi.config.article.url;
          const query = layoutFromApi.config.article.query;

          if (url) {
            window.localStorage.setItem(LocalStorageKey.ARTICLE_URL, url);
          }

          if (query) {
            window.localStorage.setItem(LocalStorageKey.ARTICLE_QUERY, query);
          }
        }
      }

      return {
        layout: getLayoutHelper(layoutFromApi),
        tenant: tenant,
        articleOption: layoutFromApi.config.article,
      };
    }
    return null;
  }
  return null;
}

export async function getArticleById(
  id: string,
  _jsonata: string,
  url: string,
  useDefault?: boolean,
) {
  let resLayout = undefined;
  let tenant: string | null = localStorage.getItem(LocalStorageKey.TENANT);
  let articleUrl: string | null = localStorage.getItem(
    LocalStorageKey.ARTICLE_URL,
  );
  let articleQuery: string | null = localStorage.getItem(
    LocalStorageKey.ARTICLE_QUERY,
  );

  if (!tenant) {
    resLayout = await getLayout(false);
    if (!resLayout) {
      return [];
    }
    tenant = resLayout.tenant;
    articleUrl = resLayout.articleOption?.url ?? null;
    articleQuery = resLayout.articleOption?.query ?? null;
  }
  let actualHostName = api() || `https://${url}/api`;
  const portalJsonata = new PortalJsonata();

  if (useDefault || defaultNewsTenants.includes(tenant)) {
    const data = await axios
      .get(`https://icity.paas.ttgt.vn/ttgt-tphcm/articles/${id}`, {
        headers: {
          "x-dynamic-token": generateToken(),
        },
      })
      .catch((error) => null);
    if (!data) {
      return null;
    }
    return await portalJsonata.transform(data.data, _jsonata);
  }
  const dataList: any = await axios
    .post(
      articleUrl ?? `${actualHostName}/portals/articles/graphql`,
      {
        query:
          articleQuery ??
          `query { ehiring_articles (limit: -1) { id title author status thumbnail date_created date_updated excerpt content tenant } }`,
      },
      {
        headers: {
          "x-dynamic-token": generateToken(),
        },
      },
    )
    .catch((error) => null);

  if (!dataList) {
    return null;
  }

  if (!dataList?.data) {
    return [];
  }

  if (Array.isArray(dataList.data.ehiring_articles)) {
    const articleData: any[] = dataList.data.ehiring_articles;
    dataList.data.ehiring_articles = articleData.filter(
      (e: any) => e.tenant === tenant,
    );
  }

  const transformData = await portalJsonata.transform(dataList.data, _jsonata);

  const data = transformData?.find((dataSingleton: any) => {
    return dataSingleton.id === id;
  });

  return data || null;
}

// Get data dynamic collection
// * url là hostname truyền vào: khi call func từ useEffect thì không lấy được hostname set ở getServerProps nên phải truyền thêm hostname qua props hoặc router
// * collection chỉ định lấy data từ đâu, riêng trường hợp recruitment ở ehiring phải xử lý riêng vì cần mapping location từ masterdata
// * query dynamic
// * jsonata để format lại data theo mẫu, được nhận từ file config
export async function getDataFromCollection(
  url: string,
  collection?: string,
  query?: string,
  _jsonata?: string,
  selectedIds?: string[],
  useDefault?: boolean,
  page: number = 1,
  limit: number = 15,
) {
  let tenant: string | null = localStorage.getItem(LocalStorageKey.TENANT);
  let articleUrl: string | null = localStorage.getItem(
    LocalStorageKey.ARTICLE_URL,
  );
  let articleQuery: string | null = localStorage.getItem(
    LocalStorageKey.ARTICLE_QUERY,
  );
  let actualHostName = api() || `https://${url}/api`;

  if (!tenant) {
    const resLayout = await getLayout(false);
    if (!resLayout) {
      return [];
    }
    tenant = resLayout.tenant;
    articleUrl = resLayout.articleOption?.url ?? null;
    articleQuery = resLayout.articleOption?.query ?? null;
  }
  if (collection === "/articles") {
    let data;

    if (useDefault || defaultNewsTenants.includes(tenant)) {
      if (query !== "") {
        data = await axios
          .get(`https://icity.paas.ttgt.vn/ttgt-tphcm/articles/${query}`, {
            headers: {
              "x-dynamic-token": generateToken(),
            },
          })
          .catch((error) => null);
      } else {
        data = await axios
          .get("https://icity.paas.ttgt.vn/ttgt-tphcm/articles", {
            headers: {
              "x-dynamic-token": generateToken(),
            },
          })
          .catch((error) => null);
      }
    } else {
      data = await axios
        .post(
          articleUrl ?? `${actualHostName}/portals/articles/graphql`,
          {
            query:
              articleQuery ??
              'query { ehiring_articles(filter: { status: { _eq: "published" } }, limit: -1) { id title author status thumbnail date_created date_updated excerpt content tenant } }',
          },
          {
            headers: {
              "x-dynamic-token": generateToken(),
            },
          },
        )
        .catch((error) => null);
      if (!data?.data) {
        return [];
      }
      data.data.ehiring_articles = data.data.ehiring_articles
        .sort((a: { date_updated: string }, b: { date_updated: string }) => {
          return (
            new Date(b.date_updated).getTime() -
            new Date(a.date_updated).getTime()
          );
        })
        .map((item) => ({ ...item, date_created: item.date_updated }));
    }
    // if (!data?.data) {
    //   return [];
    // }

    if (Array.isArray(data.data.ehiring_articles)) {
      const articleData: any[] = data.data.ehiring_articles;
      data.data.ehiring_articles = articleData.filter(
        (e: any) => e.tenant === tenant,
      );
    }

    const portalJsonata = new PortalJsonata();
    const returnData = await portalJsonata.transform(
      data.data,
      _jsonata ?? "$",
    );
    if (!returnData) {
      return [];
    }
    if (Array.isArray(returnData)) {
      console.log("Selected ID...", selectedIds);
      console.log("Before...", returnData);
      const filterData = returnData.filter((item: any) => {
        if (!selectedIds) {
          return true;
        }
        if (item.id) {
          return selectedIds.includes(item.id);
        }
        if (item._id) {
          return selectedIds.includes(item._id);
        }
        return false;
      });

      console.log("After...", filterData);
      return filterData;
    }
    return returnData;
  } else {
    if (!collection) {
      return [];
    }

    let actualHostName =
      process.env.NEXT_PUBLIC_API_URL || `https://${url}/api`;

    const listData = await axios
      .get(
        `${actualHostName}/portals/${collection}?${
          query ?? ""
        }&limit=${limit}&page=${page}`,
        {
          headers: {
            "x-dynamic-token": generateToken(),
          },
        },
      )
      .catch((error) => null);
    if (collection === "recruitments") {
      const extraData = await axios
        .get(`${actualHostName}/portals/campaigns?limit=${0}&page=${page}`, {
          headers: {
            "x-dynamic-token": generateToken(),
          },
        })
        .catch((error) => null);
      if (isArray(extraData?.data?.data) && isArray(listData?.data?.data)) {
        listData.data.data = [...listData.data.data, ...extraData.data.data]
          .map((item) => {
            item.tags = item.tags.filter((i: any) => !isNil(i));
            return item;
          })
          .sort((a, b) => {
            return (
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
            );
          });
        listData.data.count =
          (listData.data.count || 0) + (extraData.data.count || 0);
        listData.data.pageCount = Math.max(
          listData.data.pageCount || 0,
          extraData.data.pageCount || 0,
        );
        listData.data.total =
          (listData.data.total || 0) + (extraData.data.total || 0);
      }
    }
    if (!listData) return null;

    const portalJsonata = new PortalJsonata();
    const dataFormatted: any[] = await (Array.isArray(listData.data.data)
      ? portalJsonata.transform(listData.data.data, _jsonata ?? "$")
      : portalJsonata.transform(listData.data, _jsonata ?? "$"));

    const masterdataKeyArray: string[] = [];

    const listDataFormatted = dataFormatted
      ? Array.isArray(dataFormatted)
        ? dataFormatted
        : [dataFormatted]
      : [];

    listDataFormatted.forEach(
      ({ location, tags }: { location: string | string[]; tags: string[] }) => {
        if (
          location &&
          !Array.isArray(location) &&
          masterdataKeyArray.length === 0
        ) {
          masterdataKeyArray.push("site");
        }
        // xử lý tags

        tags.forEach((tag: any) => {
          if (typeof tag === "number") {
            tag = tag.toString();
          }
          if (tag.includes(":")) {
            const tagInfo = tag.split(":");
            // console.log('tagInfo', tagInfo);
            if (tagInfo.length !== 3) {
              return;
            }

            const masterdataKey = tagInfo[1];
            if (!masterdataKeyArray.includes(masterdataKey))
              masterdataKeyArray.push(masterdataKey);
          } else if (!masterdataKeyArray.includes("recruitment-plan-tags")) {
            masterdataKeyArray.push("recruitment-plan-tags");
          }
        });
      },
    );

    const planCascaderData = await getMasterdata(url, "plan-cascader");

    const grouped = _(planCascaderData)
      .filter((item) => _.isEmpty(item.metadata)) // Các item gốc
      .keyBy("value") // Dùng code làm key
      .mapValues((parentItem) => {
        // Tìm các item có preCode trỏ đến item gốc
        return _.filter(planCascaderData, (childItem) =>
          _.some(childItem.metadata, { preCode: parentItem?.value }),
        );
      })
      .value();

    const resultMasterdata = await Promise.all(
      masterdataKeyArray.map(async (key) => ({
        key: key,
        data: await getMasterdata(url, key),
      })),
    );

    let masterdataValue: {
      [key: string]: { label: string; value: string; metadata: any }[];
    } = {};
    resultMasterdata.forEach(({ key, data }) => {
      masterdataValue[key] = data;
    });

    const mapOneObject = (inputObject: any) => {
      let returnObject = { ...inputObject };
      const mappedTagArray: { key: string; label: string }[] = [];
      const mapToKeyDict: { [key: string]: string[] } = {};

      _.filter(
        returnObject.tags,
        (item) => !item.includes("user-level"),
      ).forEach((tag: any) => {
        let tagLabelFind: string | undefined = undefined;
        let tagKey: string | undefined = undefined;
        let tagValue: string | undefined = undefined;
        if (typeof tag === "number") {
          tag = tag.toString();
        }
        if (tag.includes(":")) {
          const tagInfo = tag.split(":");
          if (tagInfo.length !== 3) {
            return;
          }
          tagKey = tagInfo[1] as string;
          tagValue = tagInfo[2];

          if (tag.includes("industry-sector")) {
            const test = masterdataValue[tagKey]?.find(({ value }) => {
              return value === tagValue;
            });
            returnObject.image = test?.metadata[0].img;
            console.log("test", test?.metadata[0].img);
          }
        } else {
          tagKey = "recruitment-plan-tags";
          tagValue = tag;
        }

        if (tagKey && tagValue) {
          tagLabelFind = grouped[tagKey]?.find(({ value }) => {
            return value === tagValue;
          })?.label;

          if (tagLabelFind) {
            if (Array.isArray(returnObject.mapToKey)) {
              const mapToKey = returnObject.mapToKey.find(
                (mapToKey: any) => mapToKey.tagKey === tagKey,
              );

              if (mapToKey) {
                if (!mapToKeyDict[mapToKey.key]) {
                  mapToKeyDict[mapToKey.key] = [];
                }

                if (mapToKey.value.includes(tag)) {
                  mapToKeyDict[mapToKey.key].push(tagLabelFind ?? "");
                }
                if (mapToKey.removeFromTags) {
                  return;
                }
              }
            }
            mappedTagArray.push({ key: tagKey, label: tagLabelFind });
          }
        }
      });

      if (Array.isArray(returnObject.mapToKey)) {
        returnObject.mapToKey.forEach(
          (mapToKey: {
            key: string;
            value: string[];
            removeFromTags: boolean;
            defaultValue?: string;
          }) => {
            const valueDict = mapToKeyDict[mapToKey.key];
            if (!valueDict || valueDict.length === 0) {
              returnObject[mapToKey.key] = mapToKey.defaultValue;
            } else {
              returnObject[mapToKey.key] = valueDict.join(", ");
            }
          },
        );
      }
      if (!returnObject.location || returnObject.location === "") {
        returnObject.location =
          grouped["site"]?.find(({ label, value }) => {
            return value === returnObject.location;
          })?.label ?? "";
      }
      // console.log('huyvn mappedTagArray', mappedTagArray)
      const [benefitTags, otherTags] = _.partition(mappedTagArray, {
        key: "benefit",
      });

      if (benefitTags) {
        returnObject.benefits = benefitTags.map((e: any) => e.label);
      }
      returnObject.tags = otherTags.map((e: any) => e.label);
      returnObject.total = listData.data.pageCount;

      return returnObject;
    };

    return dataFormatted
      ? Array.isArray(dataFormatted)
        ? dataFormatted.map(mapOneObject)
        : mapOneObject(dataFormatted)
      : [];
  }
}

export async function getRecruitmentPlansOrCampaigns(
  type: "recruitments" | "campaigns",
  url: string,
  query?: string,
  jsonata?: string,
  page: number = 1,
  limit: number = 0,
  dynamicFilters: any[] = [],
) {
  // Setup query builder
  const qb = RequestQueryBuilder.create();
  const searchFilters = dynamicFilters
    ? dynamicFilters
        .map((filter) => {
          // Case 1: string
          if (typeof filter.value === "string" && filter.value.trim() !== "") {
            return {
              field: filter.key,
              operator: CondOperator.CONTAINS,
              value: filter.value,
            };
          }
          // Case 2: array
          else if (Array.isArray(filter.value) && filter.value.length > 0) {
            let labels;
            getMasterdata(url, filter.masterdata.key).then((res) => {
              labels = res.map((r) => r.label);
            });
            return {
              field: filter.key,
              operator: CondOperator.IN,
              value: filter.value,
            };
          }
          return undefined;
        })
        .filter((f) => f !== undefined)
    : [];
  qb.setFilter(searchFilters);
  qb.setPage(page);
  qb.setLimit(limit);

  const finalQuery = qb.query() + `${query !== "" ? "&" + query : ""}`;
  // Finalize and fetch with API url
  let actualHostName = api() || `https://${url}/api`;
  let apiUrl = `${actualHostName}/portals/${type}?${finalQuery}`;
  let data: any = await axios
    .get(apiUrl, {
      headers: {
        "x-dynamic-token": generateToken(),
      },
    })
    .then((res) => res.data)
    .catch((_) => null);

  const transformedData = await new PortalJsonata().transform(
    data.data,
    jsonata ?? "$",
  );

  const masterdataKeyArray: string[] = [];
  transformedData.forEach(
    ({ location, tags }: { location: string | string[]; tags: string[] }) => {
      if (
        location &&
        !Array.isArray(location) &&
        masterdataKeyArray.length === 0
      ) {
        masterdataKeyArray.push("site");
      }

      tags.forEach((tag: any) => {
        if (typeof tag === "number") {
          tag = tag.toString();
        }
        if (tag.includes(":")) {
          const tagInfo = tag.split(":");
          if (tagInfo.length !== 3) {
            return;
          }

          const masterdataKey = tagInfo[1];
          if (!masterdataKeyArray.includes(masterdataKey))
            masterdataKeyArray.push(masterdataKey);
        } else if (!masterdataKeyArray.includes("recruitment-plan-tags")) {
          masterdataKeyArray.push("recruitment-plan-tags");
        }
      });
    },
  );

  const planCascaderData = await getMasterdata(url, "plan-cascader");

  const grouped = _(planCascaderData)
    .filter((item) => _.isEmpty(item.metadata))
    .keyBy("value")
    .mapValues((parentItem) => {
      return _.filter(planCascaderData, (childItem) =>
        _.some(childItem.metadata, { preCode: parentItem?.value }),
      );
    })
    .value();

  const resultMasterdata = await Promise.all(
    masterdataKeyArray.map(async (key) => ({
      key: key,
      data: await getMasterdata(url, key),
    })),
  );

  let masterdataValue: {
    [key: string]: { label: string; value: string; metadata: any }[];
  } = {};
  resultMasterdata.forEach(({ key, data }) => {
    masterdataValue[key] = data;
  });

  const mapOneObject = (inputObject: any) => {
    let returnObject = { ...inputObject };
    const mappedTagArray: { key: string; label: string }[] = [];
    const mapToKeyDict: { [key: string]: string[] } = {};

    _.filter(returnObject.tags, (item) => !item.includes("user-level")).forEach(
      (tag: any) => {
        let tagLabelFind: string | undefined = undefined;
        let tagKey: string | undefined = undefined;
        let tagValue: string | undefined = undefined;
        if (typeof tag === "number") {
          tag = tag.toString();
        }
        if (tag.includes(":")) {
          const tagInfo = tag.split(":");
          if (tagInfo.length !== 3) {
            return;
          }
          tagKey = tagInfo[1] as string;
          tagValue = tagInfo[2];

          if (tag.includes("industry-sector")) {
            const test = masterdataValue[tagKey]?.find(({ value }) => {
              return value === tagValue;
            });
            returnObject.image = test?.metadata[0].img;
          }
        } else {
          tagKey = "recruitment-plan-tags";
          tagValue = tag;
        }

        if (tagKey && tagValue) {
          tagLabelFind = grouped[tagKey]?.find(({ value }) => {
            return value === tagValue;
          })?.label;

          if (tagLabelFind) {
            if (Array.isArray(returnObject.mapToKey)) {
              const mapToKey = returnObject.mapToKey.find(
                (mapToKey: any) => mapToKey.tagKey === tagKey,
              );

              if (mapToKey) {
                if (!mapToKeyDict[mapToKey.key]) {
                  mapToKeyDict[mapToKey.key] = [];
                }

                if (mapToKey.value.includes(tag)) {
                  mapToKeyDict[mapToKey.key].push(tagLabelFind ?? "");
                }
                if (mapToKey.removeFromTags) {
                  return;
                }
              }
            }
            mappedTagArray.push({ key: tagKey, label: tagLabelFind });
          }
        }
      },
    );

    if (Array.isArray(returnObject.mapToKey)) {
      returnObject.mapToKey.forEach(
        (mapToKey: {
          key: string;
          value: string[];
          removeFromTags: boolean;
          defaultValue?: string;
        }) => {
          const valueDict = mapToKeyDict[mapToKey.key];
          if (!valueDict || valueDict.length === 0) {
            returnObject[mapToKey.key] = mapToKey.defaultValue;
          } else {
            returnObject[mapToKey.key] = valueDict.join(", ");
          }
        },
      );
    }
    if (!returnObject.location || returnObject.location === "") {
      returnObject.location =
        grouped["site"]?.find(({ label, value }) => {
          return value === returnObject.location;
        })?.label ?? "";
    }
    const [benefitTags, otherTags] = _.partition(mappedTagArray, {
      key: "benefit",
    });

    if (benefitTags) {
      returnObject.benefits = benefitTags.map((e: any) => e.label);
    }
    returnObject.tags = otherTags.map((e: any) => e.label);
    returnObject.total = data.pageCount;

    return returnObject;
  };

  data.data = transformedData.map(mapOneObject);
  return data;
}

export async function uploadFile(file: File) {
  let bodyFormData = new FormData();
  bodyFormData.append("file", file);
  return axios.post(
    "https://cf66-2a09-bac5-d41d-18be-00-277-4b.ap.ngrok.io/api/portals/file-manager",
    bodyFormData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        "Access-Control-Allow-Origin": "*",
        "x-dynamic-token": generateToken(),
      },
    },
  );
}

export async function postTalent(url: string, talent: any, _file: File) {
  if (_file) {
    let fileRes;
    fileRes = await uploadFile(_file);
    talent.cv.push(fileRes.data._id);
    return axios
      .post(
        `${
          process.env.NEXT_PUBLIC_API_URL
            ? process.env.NEXT_PUBLIC_API_URL
            : url
        }/portals/talents`,
        talent,
        {
          headers: {
            "x-dynamic-token": generateToken(),
          },
        },
      )
      .catch((err) => err.message);
  } else {
    return axios
      .post(
        `${
          process.env.NEXT_PUBLIC_API_URL
            ? process.env.NEXT_PUBLIC_API_URL
            : url
        }/portals/talents`,
        talent,
        {
          headers: {
            "x-dynamic-token": generateToken(),
          },
        },
      )
      .catch((err) => err.message);
  }
}

type MasterdataMetaData = Array<{ type: string }>;

interface MasterdataType {
  code: string;
  name: string;
  metadata: MasterdataMetaData;
}

export async function getMasterdata(
  url: string,
  masterdata: string,
  metadataType?: string,
  locale: string = "vi",
): Promise<
  Array<{
    value: string;
    label: string;
    metadata: MasterdataMetaData;
  }>
> {
  const filterMasterdata = (
    masterdataArray: {
      value: string;
      label: string;
      metadata: MasterdataMetaData;
    }[],
  ) => {
    if (!metadataType) {
      return masterdataArray;
    }

    return masterdataArray.filter(({ metadata }) => {
      return metadata.find(
        (metadataSingle) => metadataSingle.type === metadataType,
      );
    });
  };

  let actualHostName = process.env.NEXT_PUBLIC_API_URL
    ? process.env.NEXT_PUBLIC_API_URL
    : `/api`;

  let actualMasterdata = localStorage.getItem(`masterdata-${masterdata}`);

  if (actualMasterdata) {
    return filterMasterdata(JSON.parse(actualMasterdata));
  }

  let resLayout = undefined;
  let tenant: string | null = localStorage.getItem(LocalStorageKey.TENANT);
  if (!tenant) {
    resLayout = await getLayout(false);
    if (!resLayout) {
      return [];
    }
    tenant = resLayout.tenant;
  }
  const qb = RequestQueryBuilder.create();
  if (tenant.length > 0) {
    qb.setFilter({
      field: "tenant",
      operator: "$eq",
      value: tenant,
    });
  }

  const res: AxiosResponse<{ _id: string; data: MasterdataType[] }> | null =
    await axios
      .get(
        `${actualHostName}/portals/masterdata/${masterdata}/data?${qb.query()}`,
        {
          headers: {
            "x-dynamic-token": generateToken(),
            "X-Language": locale,
          },
        },
      )
      .catch((error) => {
        return null;
      });

  if (res) {
    const returnValue = res.data.data.map((e: MasterdataType) => ({
      value: e.code,
      label: e.name,
      metadata: e.metadata,
    }));

    localStorage.setItem(
      `masterdata-${masterdata}`,
      JSON.stringify(returnValue),
    );

    if (tenant.toLowerCase().includes("vietbank")) {
      returnValue.sort((a, b) => {
        const priority = ["HN", "HCM"];

        const aIndex = priority.indexOf(a.value);
        const bIndex = priority.indexOf(b.value);

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        if (aIndex !== -1) {
          return -1;
        }
        if (bIndex !== -1) {
          return 1;
        }
        return a.label.localeCompare(b.label, "vi");
      });
    }
    return filterMasterdata(returnValue);
  }
  localStorage.setItem(`masterdata-${masterdata}`, JSON.stringify([]));

  return [];
}

export async function getTalentByCode(url: string, code: string) {
  let actualHostName = process.env.NEXT_PUBLIC_API_URL || `https://${url}/api`;
  const data = await axios
    .get(`${actualHostName}/portals/applications/${code}/schedules`, {
      headers: {
        "x-dynamic-token": generateToken(),
      },
    })
    .catch((error) => null);
  if (!data) return null;
  const masterdataLocation = await getMasterdata(url, "location");
  const masterdataExaminationMethod = await getMasterdata(
    url,
    "examination-method",
  );
  const masterdataInterviewMethod = await getMasterdata(
    url,
    "interview-method",
  );
  data.data.schedules = mappingArrayObj(
    data.data.schedules,
    masterdataLocation,
    "location",
  );
  const interviewArr = mappingArrayObj(
    data.data.schedules.filter(
      (cond: { scheduleType: string }) => cond.scheduleType == "Interview",
    ),
    masterdataInterviewMethod,
    "scheduleMethod",
  );
  const examinationArr = mappingArrayObj(
    data.data.schedules.filter(
      (cond: { scheduleType: string }) => cond.scheduleType == "Examination",
    ),
    masterdataExaminationMethod,
    "scheduleMethod",
  );
  data.data.schedules = interviewArr.concat(examinationArr);
  data.data.schedules = data.data.schedules.filter(
    (e: any) => e.status == "pending",
  );

  data.data.recruitmentPlan = {
    name: data.data.recruitmentPlan.name,
    stages: data.data.recruitmentPlan.stages.map((ele: any) => {
      const schedules = data.data.schedules.filter(
        (e: any) => e.scheduleType == ele.type,
      );
      return { ...ele, schedules };
    }),
  };

  return data.data;
}

// arr1 là mảng data cần mapping
// arr2 là list các phần tử để so sánh mapping
function mappingArrayObj(
  arr1: Array<any>,
  arr2: Array<any>,
  keyMapping: string,
) {
  return arr1.map((e, i) => {
    let temp = arr2.find((element) => element.value === e[keyMapping]);
    if (temp) {
      e[keyMapping] = temp.label;
    }
    return e;
  });
}

export async function getTalentByCodeRefactor(
  url: string,
  code: string,
): Promise<TalentSchedule | null> {
  let actualHostName = process.env.NEXT_PUBLIC_API_URL || `https://${url}/api`;
  const data: AxiosResponse<TalentSchedule> | null = await axios
    .get(`${actualHostName}/portals/applications/${code}/schedules`, {
      headers: {
        "x-dynamic-token": generateToken(),
      },
    })
    .catch((error) => null);
  if (!data) return null;

  const talentScheduleDataReturn: TalentSchedule = { ...data.data };
  const talentScheduleDataCopy: TalentSchedule = { ...data.data };

  const masterDataLocation = await getMasterdata(url, "location");
  const masterDataExaminationMethod = await getMasterdata(
    url,
    "examination-method",
  );
  const masterDataInterviewMethod = await getMasterdata(
    url,
    "interview-method",
  );

  talentScheduleDataReturn.newSchedules =
    talentScheduleDataCopy.newSchedules.map((schedule) => {
      const returnSchedule = { ...schedule };
      const mapScheduleMethodArray =
        schedule.scheduleType === RecruitmentStageEnum.EXAMINATION
          ? [...masterDataExaminationMethod]
          : [...masterDataInterviewMethod];

      const findMapScheduleMethod = mapScheduleMethodArray.find(
        (e) => e.value === schedule.scheduleMethod,
      );
      returnSchedule.mappedScheduleMethod = findMapScheduleMethod
        ? findMapScheduleMethod.label
        : "";

      const findMapLocation = masterDataLocation.find(
        (e) => e.value === schedule.location,
      );
      returnSchedule.mappedLocation = findMapLocation
        ? findMapLocation.label
        : "";

      return returnSchedule;
    });
  const allRecruitmentStages = [
    ...talentScheduleDataCopy.recruitmentPlan.beginningStages,
    ...talentScheduleDataCopy.recruitmentPlan.stages,
    ...talentScheduleDataCopy.recruitmentPlan.endingStages,
  ];

  talentScheduleDataReturn.mappedRecruitmentPlanStages =
    allRecruitmentStages.map((recruitmentStage, index) => {
      return {
        stage: recruitmentStage,
        schedules: talentScheduleDataReturn.newSchedules.filter((e) => {
          return (
            e.currentStage === index && e.scheduleType === recruitmentStage.type
          );
        }),
      };
    });

  talentScheduleDataReturn.mappedCurrentStage =
    talentScheduleDataCopy.currentStage;
  if (talentScheduleDataCopy.offer) {
    switch (talentScheduleDataCopy.offer.status) {
      case OfferStatusEnum.APPROVED:
        talentScheduleDataReturn.status = TalentScheduleStatusEnum.ACCEPTED;
        talentScheduleDataReturn.mappedOfferStatus = OfferStatusEnum.APPROVED;
        talentScheduleDataReturn.mappedCurrentStage += 1;
        break;
      case OfferStatusEnum.REJECTED:
        talentScheduleDataReturn.status = TalentScheduleStatusEnum.REJECTED;
        talentScheduleDataReturn.mappedOfferStatus = OfferStatusEnum.REJECTED;
        break;

      default:
        talentScheduleDataReturn.mappedOfferStatus =
          OfferStatusEnum.IN_PROGRESS;
        break;
    }
  }
  return talentScheduleDataReturn;
}

export async function getOffer(hostNameUrl: string, code: string) {
  let actualHostName = api() || `https://${hostNameUrl}/api`;
  console.log("fetching offer with hostname", actualHostName);
  const uri = `${actualHostName}/portals/applications/${code}/offer`;
  const res = await axios
    .get(uri, {
      headers: {
        "x-dynamic-token": generateToken(),
      },
    })
    .catch((error) => null);
  if (!res) return null;
  return res.data;
}

export async function acceptOffer(
  hostNameUrl: string,
  code: string,
  body?: any,
) {
  let actualHostName = api() || `https://${hostNameUrl}/api`;
  const uri = `${actualHostName}/portals/applications/${code}/offer`;
  const res = await axios
    .patch(
      uri,
      { status: "accepted", extra: body },
      {
        headers: {
          "x-dynamic-token": generateToken(),
        },
      },
    )
    .catch((_error) => null);
  if (!res) return null;
  return res.data;
}
export async function rejectOffer(
  hostNameUrl: string,
  code: string,
  body?: any,
) {
  let actualHostName = api() || `https://${hostNameUrl}/api`;
  const uri = `${actualHostName}/portals/applications/${code}/offer`;
  const res = await axios
    .patch(
      uri,
      { status: "rejected", extra: body },
      {
        headers: {
          "x-dynamic-token": generateToken(),
        },
      },
    )
    .catch((_error) => null);
  if (!res) return null;
  return res.data;
}

export async function getInterview(hostNameUrl: string, scheduleId: string) {
  // const qb = RequestQueryBuilder.create();
  // const filterArray: (QueryFilter | QueryFilterArr)[] = [
  //   {
  //     field: "userId",
  //     operator: "$eq",
  //     value: userId,
  //   },
  // ];
  // qb.setFilter(filterArray);
  let actualHostName = api() || `https://${hostNameUrl}/api`;
  const uri = `${actualHostName}/portals/schedules/${scheduleId}`;
  const res = await axios
    .get(uri, {
      headers: {
        "x-dynamic-token": generateToken(),
      },
    })
    .catch((error) => null);
  if (!res) return null;
  return res.data;
}
export async function acceptInterview(hostNameUrl: string, id: string) {
  let actualHostName = api() || `https://${hostNameUrl}/api`;
  console.log("accepting interview with hostname", actualHostName);
  const uri = `${actualHostName}/portals/schedules/${id}/accept`;
  const res = await axios
    .patch(
      uri,
      {},
      {
        headers: {
          "x-dynamic-token": generateToken(),
        },
      },
    )
    .catch((error) => null);
  if (!res) return null;
  return res.data;
}
export async function rejectInterview(
  hostNameUrl: string,
  id: string,
  note: string = "",
) {
  let actualHostName = api() || `https://${hostNameUrl}/api`;
  console.log("rejecting interview with hsotname", actualHostName);
  const uri = `${actualHostName}/portals/schedules/${id}/reject`;
  const res = await axios
    .patch(
      uri,
      { note },
      {
        headers: {
          "x-dynamic-token": generateToken(),
        },
      },
    )
    .catch((error) => null);
  if (!res) return null;
  return res.data;
}
